import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Image,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import { useDismissable } from '@/context/DismissableContext';

import { DateModalSurgery } from '../../prompts/DateModal';
import { SetDelivery } from '../../prompts/SetDelivery';

import { MATERNITY_ASSETS } from './maternity-assets';

interface PreopCardMatProps {
  dueDate: Dayjs | null;
  headerText: string;
  startOfDayInHospitalTz: Dayjs | null;
}

const DISMISS_DUE_DATE_ID = 'set_due_date';
const DISMISS_CONFIRM_DELIVERY_ID = 'set_confirm_delivery';

export function PreopCardMat({
  dueDate,
  headerText,
  startOfDayInHospitalTz,
}: PreopCardMatProps) {
  const { t } = useTranslation('home');
  const [delayDateModal, toggleDelayDateModal] = useState<boolean>(false);
  const [deliveryModal, toggleDeliveryModal] = useState<boolean>(false);
  const { sessionDismissed, dismissAlert } = useDismissable();

  const hasDueDate = !_.isEmpty(dueDate);

  const conceptionDate = dayjs(dueDate).subtract(280, 'd');

  const daysPregnant = dayjs(startOfDayInHospitalTz).diff(
    dayjs(conceptionDate),
    'days'
  );

  return (
    <>
      <DateModalSurgery
        isOpen={delayDateModal}
        onClose={() => toggleDelayDateModal(false)}
        title={t('preopcardmat.button.set_future_surgery_description')}
      />
      <SetDelivery
        isOpen={deliveryModal}
        onClose={() => toggleDeliveryModal(false)}
      />
      <Card shadow="none" className="bg-transparent px-0">
        <CardHeader className="flex flex-col items-start px-0">
          <h1 className="text-2xl text-left">{headerText}</h1>
          <h2 className="text-2xl">
            {hasDueDate ? (
              <span>
                {t('preopcardmat.title_date', {
                  date: dayjs(dueDate).format('LL'),
                })}
              </span>
            ) : (
              <span>{t('preopcardmat.title_nodate')}</span>
            )}
          </h2>
        </CardHeader>
        <CardBody className="px-0">
          <MaternityCounter
            dueDate={dueDate}
            startOfDayInHospitalTz={startOfDayInHospitalTz}
          />
        </CardBody>
      </Card>

      {(!daysPregnant || daysPregnant < 280) && (
        <>
          <Alert
            color="primary"
            description={t('preopcardmat.button.set_future_surgery_title')}
            icon={<Icon icon="fa-solid:calendar-day" width="20" />}
            isVisible={!sessionDismissed[DISMISS_DUE_DATE_ID]}
            hideIconWrapper
            variant="faded"
          >
            <div className="inline-flex mt-4">
              <Button
                color="primary"
                onPress={() => toggleDelayDateModal(true)}
                size="sm"
              >
                {t('preopcardmat.button.set_future_surgery_description')}
              </Button>
              <Button
                className="ml-4"
                onPress={() => dismissAlert(DISMISS_DUE_DATE_ID)}
                variant="light"
                size="sm"
              >
                {t('common.action.dismiss')}
              </Button>
            </div>
          </Alert>
          <Alert
            color="primary"
            description={t('preopcardmat.button.confirm_surgery_title')}
            icon={<Icon icon="bi:calendar-event-fill" />}
            isVisible={!sessionDismissed[DISMISS_CONFIRM_DELIVERY_ID]}
            hideIconWrapper
            variant="faded"
          >
            <div className="inline-flex mt-4">
              <Button
                color="primary"
                onPress={() => toggleDeliveryModal(true)}
                size="sm"
              >
                {t('preopcardmat.button.confirm_surgery_description')}
              </Button>
              <Button
                className="ml-4"
                onPress={() => dismissAlert(DISMISS_CONFIRM_DELIVERY_ID)}
                variant="light"
                size="sm"
              >
                {t('common.action.dismiss')}
              </Button>
            </div>
          </Alert>
        </>
      )}
      {daysPregnant >= 280 && (
        <Alert
          color="primary"
          description={t('preopcardmat.button.set_past_surgery_title')}
          icon={<Icon icon="bi:calendar-event-fill" />}
          hideIconWrapper
          variant="faded"
        >
          <div className="inline-flex mt-4">
            <Button
              color="primary"
              onPress={() => toggleDeliveryModal(true)}
              size="sm"
            >
              {t('preopcardmat.button.confirm_surgery_description')}
            </Button>
          </div>
        </Alert>
      )}
    </>
  );
}

interface MaternityCounterProps {
  dueDate: Dayjs | null;
  startOfDayInHospitalTz: Dayjs | null;
}

export function MaternityCounter({
  dueDate,
  startOfDayInHospitalTz,
}: MaternityCounterProps) {
  const [counter, setCounter] = useState(0);
  const { t } = useTranslation('home');

  const hasDueDate = !_.isEmpty(dueDate);

  const conceptionDate = dayjs(dueDate).subtract(280, 'd');

  const daysPregnant = dayjs(startOfDayInHospitalTz).diff(
    dayjs(conceptionDate),
    'days'
  );

  useEffect(() => {
    if (!hasDueDate) {
      return;
    }

    const weeksPregnant = Math.floor(daysPregnant / 7);

    if (weeksPregnant >= 40) {
      setCounter(40);
    } else {
      setCounter(weeksPregnant);
    }
  }, [conceptionDate, dueDate, hasDueDate]);

  const isDue = dayjs(startOfDayInHospitalTz).isSame(dayjs(dueDate), 'day');

  let description = t('preopcardmat.description_nodate');

  if (isDue) {
    description = t('preopcardmat.countdown_zero');
  } else if (daysPregnant) {
    if (daysPregnant <= 7) {
      description = t('preopcardmat.countdown_day', { count: daysPregnant });
    } else {
      const weeksPregnant = Math.floor(daysPregnant / 7);
      description = t('preopcardmat.countdown_week', { count: weeksPregnant });
    }
  }

  const counterDescription = MATERNITY_ASSETS[counter]?.alt;
  const path = MATERNITY_ASSETS[counter]?.src;

  return (
    <div className="inline-flex items-center gap-4">
      {path && <Image alt="" height={50} src={path} width={50} />}
      <div className="flex flex-col items-start justify-center">
        <p className="font-light">{description}</p>
        {counterDescription && (
          <p className="font-light">{t(counterDescription)}</p>
        )}
      </div>
    </div>
  );
}
